<script setup lang="ts">
import {computed, nextTick, ref, watch} from "vue";
import {useTextareaAutosize} from "@vueuse/core";
import {Send, Square} from "@lucide/vue";
import {storeToRefs} from "pinia";
import {Textarea} from "@/core/components/ui/textarea";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import useTypewriter from "@/ai-assistant/composables/useTypewriter";
import {MAX_PROMPT_CHARACTERS, QUESTION_EXAMPLES} from "@/ai-assistant/constants";

const {isStreaming} = defineProps<{
    isStreaming: boolean;
}>();

const emits = defineEmits<{
    (event: 'send', question: string): void;
    (event: 'stop'): void;
}>();

const store = useAiAssistantStore();
const {draft} = storeToRefs(store);

const question = ref<string>('');
const focused = ref<boolean>(false);
const field = ref();

/**
 * The count, shown only once the end is in sight.
 *
 * Always visible it would be a meter on a field nobody fills: questions run to
 * a line or two. It appears where it starts to matter, which is where a
 * pharmacist is about to lose the end of what they are typing.
 */
const remaining = computed<number | null>(() => {
    const left = MAX_PROMPT_CHARACTERS - question.value.length;

    return left <= 200 ? left : null;
});

/**
 * A question opened elsewhere lands in the field, focused at its end.
 *
 * Never sent: « demander un ajustement » writes the beginning of a sentence
 * the pharmacist finishes, which keeps every request theirs.
 *
 * The caret is placed on the `<textarea>` itself: the ref holds the shared
 * Textarea component, whose single root element carries the selection API.
 */
watch(draft, async value => {
    if (!value) {
        return;
    }

    question.value = value;
    store.setDraft('');

    await nextTick();

    const element = field.value?.$el as HTMLTextAreaElement | undefined;

    element?.focus();
    element?.setSelectionRange(value.length, value.length);
});

/**
 * Taking the field is moving on: an answer still writing itself shows whole.
 */
const focus = () => {
    focused.value = true;
    store.revealAnswers();
};

/**
 * The examples only write themselves while the field is untouched: text moving
 * under the cursor while the pharmacist composes their question is a nuisance.
 */
const idle = computed<boolean>(() => !focused.value && !question.value);

const {text: typed} = useTypewriter(QUESTION_EXAMPLES, idle);

const placeholder = computed<string>(() => idle.value ? typed.value : 'Posez votre question…');

/**
 * Grow the field to its content so a long question stays readable whole; the
 * `max-h` below caps it at roughly six lines, past which the field scrolls.
 */
useTextareaAutosize({input: question, element: computed(() => field.value?.$el)});

const send = () => {
    const asked = question.value.trim();

    if (!asked || isStreaming) {
        return;
    }

    question.value = '';
    emits('send', asked);
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <p v-if="remaining !== null" class="px-0.5 text-right text-[10px] tabular-nums text-gray-dark/50">
      {{ remaining }} caractères restants
    </p>

    <!-- Aligned to the bottom so the buttons stay level with the last line. -->
    <div class="flex items-end gap-2">
      <!--
        `block` undoes the shared Textarea's `display: flex`, which throws
        `scrollHeight` off and leaves the field shorter than its own text. The
        accessible name is fixed on purpose: the placeholder is animated, and a
        screen reader must not be told the field is renamed at every keystroke.
      -->
      <Textarea
        ref="field"
        v-model="question"
        :placeholder="placeholder"
        aria-label="Votre question"
        class="no-scrollbar block max-h-[8.25rem] min-h-14 min-w-0 flex-1 resize-none overflow-y-auto rounded-lg bg-white leading-5 transition-colors"
        :maxlength="MAX_PROMPT_CHARACTERS"
        :disabled="isStreaming"
        @focus="focus"
        @blur="focused = false"
        @keydown.enter.exact.prevent="send"
      />
      <button
        v-if="isStreaming"
        class="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-dark/60 transition-colors hover:border-gray-300"
        title="Arrêter la réponse"
        @click="emits('stop')"
      >
        <Square class="size-3.5" />
      </button>
      <button
        v-else
        class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        :disabled="!question.trim()"
        title="Envoyer"
        @click="send"
      >
        <Send class="size-4" />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* The field caps at six lines and then scrolls; the bar adds noise in a panel
   this narrow, and the caret already says where one is. Same declarations as
   ScrollableArea. */
.no-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}
</style>
